import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { useApi } from "../../hooks/useApi";

function StatCard({ label, value }) {
  return (
    <div data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-surface border border-border p-5">
      <p className="text-xs uppercase tracking-wide text-white/70">{label}</p>
      <p className="font-subtitle text-3xl mt-2">{value ?? "—"}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const api = useApi();
  const { data, isFetching, isError, dataUpdatedAt, refetch } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const genuineCards = [
    ["Genuine Customers", data?.customers?.genuine?.registered],
    ["Genuine Verified", data?.customers?.genuine?.verified],
    ["Genuine Active Entitlements", data?.entitlements?.genuine_active],
    ["Genuine Leads", data?.leads?.genuine],
    ["Security Failures \u2014 Genuine (24h)", data?.security?.failures_24h_genuine],
    ["Administrators", data?.administrators],
  ];

  const devCards = [
    ["Test/Dev Accounts", data?.customers?.development?.registered],
    ["Test/Dev Verified", data?.customers?.development?.verified],
    ["Test/Dev Entitlements", data?.entitlements?.development_active],
    ["Test/Dev Leads", data?.leads?.development],
    ["Test/Dev Failures (24h)", data?.security?.failures_24h_development],
    ["Unattributed Failures (24h)", data?.security?.failures_24h_unattributed],
  ];

  const catalogCards = [
    ["Episodes", data?.episodes],
    ["Books", data?.books],
    ["Audio Editions", data?.audio],
    ["Gold Content", data?.gold],
    ["School Lessons", data?.lessons],
    ["Pending Review", data?.pending_review],
    ["Rights Unresolved", data?.rights_unresolved],
  ];

  return (
    <div data-testid="admin-dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="font-display text-display-xs">Dashboard</h1>
        <div className="flex items-center gap-3 text-xs text-white/50">
          {data?.generated_at && (
            <span data-testid="admin-dashboard-last-updated">
              Last updated {new Date(data.generated_at).toLocaleTimeString()}
              {isFetching ? " · refreshing…" : ""}
            </span>
          )}
          <button
            data-testid="admin-dashboard-refresh-btn"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 border border-white/20 px-3 py-1.5 hover:border-white/50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>
      <p className="text-white/50 text-xs mb-6">Auto-refreshes every 60 seconds while this tab is active. Every figure is a live database query — never hardcoded.</p>

      {isError && (
        <div data-testid="admin-dashboard-error" className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3 mb-6">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Could not load dashboard statistics right now. Figures below may be stale or unavailable — try Refresh.
        </div>
      )}

      <h2 className="text-xs uppercase tracking-wide text-white/50 mb-3">Genuine Business Activity</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {genuineCards.map(([label, value]) => <StatCard key={label} label={label} value={isError ? undefined : value} />)}
      </div>

      <h2 className="text-xs uppercase tracking-wide text-white/50 mb-3">Development &amp; Test Records <span className="normal-case text-white/30">(excluded from the figures above)</span></h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 opacity-70">
        {devCards.map(([label, value]) => <StatCard key={label} label={label} value={isError ? undefined : value} />)}
      </div>

      <h2 className="text-xs uppercase tracking-wide text-white/50 mb-3">Content &amp; Curriculum Inventory</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
        {catalogCards.map(([label, value]) => <StatCard key={label} label={label} value={isError ? undefined : value} />)}
      </div>

      <div className="flex flex-wrap gap-3">
        <Link data-testid="admin-dashboard-link-customers" to="customers" className="text-xs uppercase tracking-wide border border-white/20 px-4 py-3 hover:border-white/50 transition-colors">
          Open Customer Directory
        </Link>
        <Link data-testid="admin-dashboard-link-security" to="security" className="text-xs uppercase tracking-wide border border-white/20 px-4 py-3 hover:border-white/50 transition-colors">
          Open Security Log
        </Link>
      </div>
    </div>
  );
}
